import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { organizationAPI, creditsAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const { isAuthenticated, setUser } = useAuth();
  const [org, setOrg] = useState(null);
  const [credits, setCredits] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOrg = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!isAuthenticated) {
      setOrg(null);
      setCredits(null);
      setEntities([]);
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const [orgRes, creditsRes, entitiesRes] = await Promise.all([
        organizationAPI.get(),
        creditsAPI.get().catch(() => ({ data: null })),
        organizationAPI.getEntities().catch(() => ({ data: { entities: [] } })),
      ]);
      setOrg(orgRes.data);
      setCredits(creditsRes.data);
      setEntities(entitiesRes.data?.entities || []);
    } catch {
      setOrg(null);
      setCredits(null);
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refreshOrg = useCallback(() => loadOrg({ silent: true }), [loadOrg]);

  const refreshCredits = useCallback(async () => {
    try {
      const res = await creditsAPI.get();
      setCredits(res.data);
    } catch { /* ignore */ }
  }, []);

  const switchEntity = useCallback(async (orgId) => {
    try {
      const res = await organizationAPI.switchEntity(orgId);
      const { user: userData } = res.data;
      // Update user in auth context and localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      if (setUser) setUser(userData);
      // Reload org data for the new entity
      await loadOrg();
      return true;
    } catch {
      return false;
    }
  }, [loadOrg, setUser]);

  useEffect(() => {
    loadOrg();
  }, [loadOrg]);

  const isPersonal = org?.entity_type === 'personal';
  const isBusiness = org?.entity_type === 'business';
  const isVatRegistered = isBusiness && org?.vat_registered;
  const tier = org?.tier || 'free';
  const isFree = tier === 'free';
  const isPro = tier === 'pro' || tier === 'business' || tier === 'enterprise';
  const isEnterprise = tier === 'enterprise';
  const maxEntities = { free: 1, starter: 1, pro: 2, business: 3, enterprise: 10 }[tier] || 1;
  const canCreateEntity = entities.length < maxEntities;

  return (
    <OrgContext.Provider value={{
      org, loading, isPersonal, isBusiness, isVatRegistered,
      tier, isFree, isPro, isEnterprise,
      credits, refreshCredits,
      refreshOrg,
      entities, switchEntity, canCreateEntity, maxEntities,
    }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) throw new Error('useOrg must be used within an OrgProvider');
  return context;
}
